import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaIndustry, FaCogs, FaProjectDiagram, FaCloudUploadAlt, FaRobot, FaHardHat,
    FaArrowRight, FaCheckCircle, FaRulerCombined, FaNetworkWired
} from 'react-icons/fa';
import heroImage from '../../assets/Descargar amplio ángulo ver de un acero alto subir edificio en un moderno ciudad representando éxito y futurista arquitectura en el negocio industria gratis.png';

const IndustrialPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaRobot, title: 'Smart Factories', desc: 'Deploying robots and cobots for automated assembly lines.', image: 'https://images.unsplash.com/photo-1565514020176-db7936aaf9d8?w=800&q=80' },
        { icon: FaProjectDiagram, title: 'Digital Twins', desc: 'Real-time 3D simulation of physical assets to optimize performance.', image: 'https://images.unsplash.com/photo-1617791160536-598cf32026fb?w=800&q=80' },
        { icon: FaCogs, title: 'Predictive Maintenance', desc: 'IoT sensors to detect failures before they cause downtime.', image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80' },
        { icon: FaHardHat, title: 'Connected Workers', desc: 'AR/VR wearables for remote expert guidance and safety.', image: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=800&q=80' },
        { icon: FaCloudUploadAlt, title: 'Cloud Manufacturing', desc: 'Secure data lakes for multi-site production visibility.', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80' },
        { icon: FaRulerCombined, title: 'Supply Chain Visibility', desc: 'Track-and-trace solutions from raw material to delivery.', image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80' }
    ];

    const faqs = [
        { q: 'How do you secure OT networks?', a: 'We implement Purdue Model architecture, segmenting IT and OT networks with industrial DMZs and firewalls.' },
        { q: 'Can you integrate with legacy PLCs?', a: 'Yes, we use protocol converters (OPC UA, MQTT) to extract data from legacy Siemens/Allen-Bradley controllers.' },
        { q: 'What is the ROI on Digital Twins?', a: 'Typically 15-20% reduction in maintenance costs and 10% increase in throughput within the first year.' },
        { q: 'Do you offer predictive maintenance?', a: 'Yes, our AI models analyze vibration and temperature data to predict equipment failures weeks in advance.' },
        { q: 'Is your cloud solution compliant?', a: 'Our platforms are ISO 27001 certified and compliant with industry standards like NIST and IEC 62443.' },
        { q: 'How long does implementation take?', a: 'A typical pilot takes 8-12 weeks, while full-scale digital transformation programs span 12-24 months.' }
    ];

    return (
        <div className="bg-white font-sans text-slate-900">
            {/* Hero Section - Clean Slate/Steel Theme */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-top"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/70 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white drop-shadow-md"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-slate-200 mb-4">Industries &gt; Manufacturing</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Industrial <span className="font-bold">Evolution</span></h1>
                        <p className="text-lg font-light text-slate-100 leading-relaxed mb-8">
                            Connecting machines, data, and people for the Industry 4.0 era.
                        </p>
                        <div className="flex gap-4">
                            <a href="#contact" className="px-8 py-3 bg-slate-800 text-white font-medium rounded-sm hover:bg-slate-700 transition-all text-sm uppercase tracking-wide border border-slate-600">
                                View Solutions
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Introduction - HCL Style Text Left, Image Right */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl font-light mb-6 text-slate-800">The Connected Enterprise</h2>
                            <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                Manufacturing is undergoing a seismic shift. Siloed operations are giving way to fully connected ecosystems where data flows seamlessly from the shop floor to the top floor.
                            </p>
                            <p className="text-slate-600 leading-relaxed mb-8">
                                Tech-Inovant empowers industrial giants with the digital backbone needed to scale, adapt, and innovate in real-time.
                            </p>
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-slate-800" />
                                    <span className="text-slate-700 font-medium">IT/OT Convergence</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-slate-800" />
                                    <span className="text-slate-700 font-medium">Zero-Trust Security</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-slate-800" />
                                    <span className="text-slate-700 font-medium">Sustainable Operations</span>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1537498425277-c283d32ef9db?w=800&q=80"
                                alt="Industrial Automation"
                                className="rounded-sm shadow-sm"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-slate-800 text-white p-6 rounded-sm shadow-lg hidden md:block">
                                <div className="text-3xl font-bold">24/7</div>
                                <div className="text-xs uppercase tracking-wider">Uptime SLA</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Grid - Clean Cards */}
            <section className="py-16 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="mb-16">
                        <h2 className="text-3xl font-light mb-4 text-slate-900">Smart Manufacturing</h2>
                        <div className="w-20 h-1 bg-slate-800"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <div key={idx} className="bg-white border border-slate-200 hover:border-slate-400 transition-colors p-0 overflow-hidden group">
                                <div className="h-48 overflow-hidden relative bg-slate-200">
                                    {/* Clear Image */}
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-8">
                                    <service.icon className="text-3xl text-slate-800 mb-4" />
                                    <h3 className="text-xl font-medium mb-3 text-slate-900">{service.title}</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                                        {service.desc}
                                    </p>
                                    <a href="#" className="inline-flex items-center text-sm font-bold text-slate-800 hover:text-slate-600 tracking-wide uppercase">
                                        Explore <FaArrowRight className="ml-2 text-xs" />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Impact Metrics - Minimalist */}
            <section className="py-16 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <h2 className="text-3xl font-light mb-8 text-center text-slate-900">Tangible Results</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { title: 'Global Pharma', stat: '+25%', desc: 'Improvement in OEE (Overall Equipment Effectiveness).' },
                            { title: 'Steel Manufacturer', stat: '-15%', desc: 'Reduction in energy consumption via AI monitoring.' },
                            { title: 'Automotive OEM', stat: '100%', desc: 'Traceability of parts across 5 continents.' }
                        ].map((caseStudy, idx) => (
                            <div key={idx} className="bg-slate-50 p-8 border-l-4 border-slate-800">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{caseStudy.title}</div>
                                <div className="text-4xl font-light text-slate-900 mb-4">{caseStudy.stat}</div>
                                <p className="text-slate-600">{caseStudy.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Section */}
            <section id="contact" className="py-12 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <h2 className="text-3xl font-light mb-8 text-slate-900">Industrial FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="bg-white border border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-slate-50"
                                        >
                                            <span className="font-medium text-slate-800">{faq.q}</span>
                                            <span className="text-slate-400">{openFaq === idx ? '-' : '+'}</span>
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">
                                                {faq.a}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-white p-8 rounded-sm shadow-sm border border-slate-200">
                            <h3 className="text-2xl font-light mb-6 text-slate-900">Start Your Transition</h3>
                            <p className="text-slate-600 mb-8">Discuss your Industry 4.0 goals with us.</p>
                            <form className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-slate-500 outline-none transition-colors" placeholder="Jane Smith" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-slate-500 outline-none transition-colors" placeholder="Manufacturing Ltd." />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-slate-500 outline-none transition-colors" placeholder="jane@mfg.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Interest</label>
                                    <select className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-slate-500 outline-none transition-colors text-slate-700">
                                        <option>Smart Factory</option>
                                        <option>Digital Twins</option>
                                        <option>IT/OT Convergence</option>
                                        <option>Supply Chain</option>
                                    </select>
                                </div>
                                <button type="submit" className="w-full bg-slate-800 text-white font-bold py-4 rounded-sm hover:bg-slate-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Request Workshop
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact CTA */}
            <section className="py-12 bg-slate-900 text-white text-center">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-light mb-6">Transform Your Factory</h2>
                    <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
                        Start your journey towards Industry 4.0 with our expert consultants.
                    </p>
                    <button className="px-8 py-3 bg-white text-slate-900 font-bold uppercase tracking-wide hover:bg-slate-200 transition-colors">
                        Book a Workshop
                    </button>
                </div>
            </section>
        </div>
    );
};

export default IndustrialPage;
